import { useTranslation } from 'react-i18next';
import { Files, Clock, Star, Users, Lock } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { FolderTreeNode } from './FolderTreeNode';
import type { ProjectFolderNodeDto, SidebarSection } from '../types';

interface ProjectFilesSidebarProps {
  folders: ProjectFolderNodeDto[];
  isLoading: boolean;
  activeFolderId: string | null;
  activeSection: SidebarSection;
  counts?: Partial<Record<SidebarSection, number>>;
  onFolderSelect: (folderId: string | null) => void;
  onSectionSelect: (section: SidebarSection) => void;
  onRenameFolder: (folderId: string, currentName: string) => void;
  onDeleteFolder: (folderId: string) => void;
  onMoveFolder: (folderId: string) => void;
}

const SECTION_ITEMS = [
  { key: 'all' as SidebarSection, icon: Files, labelKey: 'sidebar.allDocs' },
  { key: 'recent' as SidebarSection, icon: Clock, labelKey: 'sidebar.recent' },
  { key: 'favorites' as SidebarSection, icon: Star, labelKey: 'sidebar.favorites' },
  { key: 'shared' as SidebarSection, icon: Users, labelKey: 'sidebar.shared' },
  { key: 'private' as SidebarSection, icon: Lock, labelKey: 'sidebar.private' },
] as const;

export function ProjectFilesSidebar({
  folders,
  isLoading,
  activeFolderId,
  activeSection,
  counts,
  onFolderSelect,
  onSectionSelect,
  onRenameFolder,
  onDeleteFolder,
  onMoveFolder,
}: ProjectFilesSidebarProps) {
  const { t } = useTranslation('projectFiles');

  return (
    <aside
      className="flex h-full w-60 shrink-0 flex-col border-r"
      data-testid="project-files-sidebar"
    >
      <div className="flex-1 overflow-y-auto px-2 py-2">
        <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('sidebar.sections')}
        </p>

        {SECTION_ITEMS.map(({ key, icon: Icon, labelKey }) => {
          const isActive = activeSection === key && activeFolderId === null;
          const count = counts?.[key];
          return (
            <button
              key={key}
              type="button"
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
                isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted/60',
              )}
              onClick={() => {
                onSectionSelect(key);
                onFolderSelect(null);
              }}
              data-testid={`project-files-sidebar-section-${key}`}
            >
              <Icon className="size-4 shrink-0" />
              <span className="flex-1 truncate text-left">{t(labelKey)}</span>
              {count !== undefined && (
                <span className="tabular-nums text-xs text-muted-foreground">{count}</span>
              )}
            </button>
          );
        })}

        <div className="mx-3 my-2 h-px bg-border" />

        <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('sidebar.folders')}
        </p>

        <div>
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="mx-3 my-1.5 h-5 rounded" />
              ))
            : folders.map((folder) => (
                <FolderTreeNode
                  key={folder.id}
                  node={folder}
                  activeFolderId={activeFolderId}
                  onSelect={(id) => {
                    onFolderSelect(id);
                    onSectionSelect('all');
                  }}
                  onRename={onRenameFolder}
                  onDelete={onDeleteFolder}
                  onMove={onMoveFolder}
                />
              ))}
        </div>
      </div>
    </aside>
  );
}
